import { useMemo, useState } from 'react'

import { jointProvidersOf } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/jointAppointment'
import {
  getScheduleStaffColor,
  getScheduleStaffColorByIndex,
} from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/scheduleInstance'
import type { IPatientConsultationStaff } from 'features/Home/interfaces/IInfoPatient'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'

import {
  initialPeopleOf,
  lockedPersonOf,
  normalizePeople,
  peopleFields,
  primaryOf,
  smsNoteOf,
  staffIdFor,
  type PeopleContext,
  type PeopleFields,
  type SmsNote,
} from './appointmentPeople'
import type { PersonOption } from './ProviderField'

type Args = {
  open: boolean
  // Joint visits need OpenEMR; other sites book one provider.
  isEmr: boolean
  isEditing: boolean
  edited: IPatientSchedule | undefined
  // The form's consultation_staff: the primary of a new visit, the row's doctor otherwise.
  staffId: number | null | undefined
  initialStaffId: number | null | undefined
  providers: IPatientConsultationStaff[]
  railStaff?: { id: number }[]
  onStaffChange: (id: number | null) => void
}

export type AppointmentPeople = {
  multiple: boolean
  people: number[]
  // The people the dialog opened with.
  initialPeople: number[]
  options: PersonOption[]
  lockedId: number | null
  primaryId: number | null
  canMakePrimary: boolean
  fields: PeopleFields
  // The footer's "Wu, Jessica + 1".
  label: string
  // People an edit takes off the visit; their appointments are deleted.
  removedNames: string[]
  // Which copy texts the patient on a joint visit.
  smsNote: SmsNote | null
  // Several people are named when away; one doctor gets the usual warning.
  awayNamesOf: (awayIds: number[]) => string[]
  nameOf: (id: number) => string
  change: (next: number[]) => void
  makePrimary: (id: number) => void
}

export const useAppointmentPeople = ({
  open,
  isEmr,
  isEditing,
  edited,
  staffId,
  initialStaffId,
  providers,
  railStaff = [],
  onStaffChange,
}: Args): AppointmentPeople => {
  // A refetch with the same people keeps the user's picks; new people from the server replace them.
  const serverPeople = (edited?.appointment_with ?? [])
    .map((person) => person.consultation_user_id)
    .join(',')
  const initialPeople = useMemo(
    () => (isEmr ? initialPeopleOf(edited, isEditing, initialStaffId) : []),
    [open, isEmr, isEditing, edited?.id, edited?.consultation_staff, serverPeople, initialStaffId],
  )
  // Picks belong to the opening they were made in; a new opening starts from its own people.
  const [chosen, setChosen] = useState({ from: initialPeople, value: initialPeople })
  const picked = chosen.from === initialPeople ? chosen.value : initialPeople

  const jointPrimaryId = useMemo(
    () =>
      (isEditing ? jointProvidersOf(edited) : []).find((provider) => provider.isPrimary)?.id ??
      null,
    [isEditing, edited],
  )
  const context = useMemo<PeopleContext>(
    () => ({
      isEditing,
      ownId: isEditing ? edited?.consultation_staff ?? null : null,
      rowIsJoint: isEditing && (edited?.emr_group_id != null || initialPeople.length > 1),
    }),
    [isEditing, edited, initialPeople],
  )

  // Bookable providers, plus anyone already on the visit.
  const options = useMemo(() => {
    // The rail's color for whoever is on it, so a chip matches the schedule.
    const colorOf = (id: number) => {
      const index = railStaff.findIndex((staff) => staff.id === id)
      return index < 0 ? getScheduleStaffColor(id) : getScheduleStaffColorByIndex(index)
    }
    const listed = providers.map((provider) => ({
      id: provider.id,
      name: provider.name,
      role: provider.is_doctor ? 'Doctor' : 'Staff',
      color: colorOf(provider.id),
    }))
    const extra = (edited?.appointment_with ?? [])
      .filter((person) => !listed.some((option) => option.id === person.consultation_user_id))
      .map((person) => ({
        id: person.consultation_user_id,
        name: person.consultation_user_name || `Provider #${person.consultation_user_id}`,
        role: 'Staff',
        color: colorOf(person.consultation_user_id),
      }))
    return [...listed, ...extra]
  }, [providers, edited, railStaff])

  const people = isEmr ? picked : staffId ? [staffId] : []
  const primaryId = primaryOf({ people, context, staffId, jointPrimaryId })
  const nameOf = (id: number) =>
    options.find((option) => option.id === id)?.name ?? `Provider #${id}`

  const leadId = primaryId ?? people[0]
  const label = !leadId
    ? 'Provider'
    : people.length > 1
    ? `${nameOf(leadId)} + ${people.length - 1}`
    : nameOf(leadId)

  const change = (next: number[]) => {
    if (!isEmr) {
      onStaffChange(next[0] ?? null)
      return
    }
    const normalized = normalizePeople([...new Set(next)], context)
    setChosen({ from: initialPeople, value: normalized })
    const nextStaffId = staffIdFor(normalized, context, staffId)
    if (nextStaffId !== staffId) onStaffChange(nextStaffId)
  }

  return {
    multiple: isEmr,
    people,
    initialPeople,
    options,
    lockedId: lockedPersonOf(people, context),
    primaryId,
    // OpenEMR keeps an existing visit's primary.
    canMakePrimary: !isEditing && people.length > 1,
    fields: isEmr ? peopleFields({ people, context, initial: initialPeople, primaryId }) : {},
    label,
    removedNames: isEditing ? initialPeople.filter((id) => !people.includes(id)).map(nameOf) : [],
    smsNote: smsNoteOf({ people, primaryId, context, nameOf }),
    awayNamesOf: (awayIds) =>
      people.length > 1 ? people.filter((id) => awayIds.includes(id)).map(nameOf) : [],
    nameOf,
    change,
    makePrimary: onStaffChange,
  }
}
